import { type FC, memo, useCallback, useMemo } from "react";
import {
  Bar,
  BarChart as BarChartRechart,
  type BarProps,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getAlpha, getTextWidth } from "senf-shared/utils/helpers";
import { cn } from "../../../util/classNameConstructor";
import { ChartTooltip } from "../../core/charts/ChartTooltip";
import {
  Legend as CustomizedLegend,
  type LegendPayload,
} from "../../core/charts/Legend";

interface ChartDataItem {
  value: number;
  label: string;
  fill?: string;
  color?: string;
  initialOrder?: number;
}

interface ChartBar {
  value: string;
  color: string;
  fill?: string;
}

interface ChartLegend {
  value: string;
  color: string;
  initialOrder?: number;
  legendReference?: string;
  label: string;
  opacity?: number;
}

interface RenderRadiusProps {
  index: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  strokeWidth?: number;
  radius?: number;
  fill?: string;
}

interface ChartsProps {
  data: ChartDataItem[];
  bars?: ChartBar[];
  legend?: ChartLegend[];
  minBarSize?: number;
  legendReference?: string;
  className?: string;
  minHeight?: number;
  width?: number;
  showLabelsY?: boolean;
  showLabelsX?: boolean;
  labelYOrientation?: "bottom" | "top";
  direction?: "horizontal" | "vertical";
  stacked?: boolean;
  height?: number | string;
  padding?: { top: number; right: number; left: number; bottom: number };
  isRelative?: boolean;
  yAxisWidth?: number;
  isAnimationActive?: boolean;
  maxValue?: number;
  average?: number;
}

const renderRadius = (
  props: RenderRadiusProps,
  data: ChartDataItem[],
  bars: ChartBar[],
  value: string,
): React.ReactNode | null => {
  if (!props || !data || !Array.isArray(bars) || typeof value === "undefined") {
    return null;
  }

  const last = bars
    .filter((bar) => {
      return (
        bar?.value &&
        bar.value in data[props.index] &&
        data[props.index][bar.value] !== 0
      );
    })
    .pop();

  const isLast = last?.value === value;
  const { x = 0, y = 0, width = 0, height = 0 } = props;
  const border = props.strokeWidth || 0;
  const radius = isLast
    ? Math.max(0, Math.min(props.radius || 0, width - border))
    : 0;

  // Ensure all values are valid numbers
  const safeX = Number.isFinite(x) ? Number(x) : 0;
  const safeY = Number.isFinite(y) ? Number(y) : 0;
  const safeWidth = Number.isFinite(width) ? Number(width) : 0;
  const safeHeight = Number.isFinite(height) ? Number(height) : 0;
  const safeRadius = Number.isFinite(radius) ? Number(radius) : 0;
  const safeBorder = Number.isFinite(border) ? Number(border) : 0;

  // Don't render if essential dimensions are invalid
  if (safeWidth <= 0 || safeHeight <= 0) {
    return null;
  }

  return (
    <path
      fill={props.fill}
      d={`M${safeX},${safeY}
h${Math.max(safeWidth - safeRadius - safeBorder, 0)}
a${safeRadius},${safeRadius} 0 0 1 ${safeRadius},${safeRadius}
v${safeHeight - safeRadius * 2}
a${safeRadius},${safeRadius} 0 0 1 -${safeRadius},${safeRadius}
h-${Math.max(safeWidth - safeRadius - safeBorder, 0)}
z`}
      fillRule="nonzero"
      className="recharts-pathangle"
    />
  );
};

const BarChart: FC<ChartsProps> = ({
  data,
  bars,
  legend,
  minBarSize,
  legendReference,
  className,
  minHeight = 360,
  width = 500,
  maxValue,
  average,
  showLabelsY = true,
  showLabelsX = true,
  labelYOrientation = "bottom",
  direction = "horizontal",
  stacked = false,
  height,
  padding = { top: 10, right: 0, left: 0, bottom: 0 },
  isRelative = false,
  isAnimationActive = true,
}) => {
  const tickMargin = 5;
  const tickSize = 5;
  const tickSpace = tickMargin + tickSize;

  const getWidth = (value?: string) => getTextWidth(value || "", "12px Nunito");
  const isVertical = useMemo(() => direction === "vertical", [direction]);

  const getYAxisWidth = useMemo(() => {
    const width = data?.reduce((acc, curr) => {
      const label = (isVertical ? curr?.value : curr?.label)?.toString();
      return Math.min(Math.max(getWidth(label), acc), 300);
    }, 10);
    return width - (isVertical ? 0 : tickSpace);
  }, [data, direction]);

  const largestCharLength = useMemo(() => {
    return data.reduce((max, item) => {
      const label = (isVertical ? item?.label : item?.value)?.toString();
      return Math.max(max, getWidth(label));
    }, 0);
  }, [data]);

  const showLegend = useMemo(() => {
    const barWidth =
      (width - getYAxisWidth - tickMargin - tickSize) / data.length - 20;
    return barWidth < largestCharLength && direction === "vertical";
  }, [data, width, largestCharLength]);

  const legendPayload = useMemo(
    () =>
      legend?.map((item, index) => ({
        ...item,
        legendReference: showLegend
          ? getAlpha(item.initialOrder ?? index)
          : undefined,
      })),
    [legend, showLegend],
  );

  if (!data?.length) return null;

  return (
    // THIS IS A FIX FOR RESIZING

    data && (
      <div className={cn("h-full w-full duration-200 p-5 pb-2", className)}>
        <ResponsiveContainer
          width="100%"
          height={direction === "vertical" ? minHeight : height || "100%"}
          minWidth={0}
          minHeight={Math.max(
            minHeight,
            direction === "horizontal" ? data.length * (minBarSize || 0) : 0,
          )}
        >
          <BarChartRechart
            data={data}
            layout={direction === "horizontal" ? "vertical" : "horizontal"}
            margin={{
              ...padding,
              left: padding.left + 20,
              right: padding.right + 5,
            }}
          >
            {direction === "horizontal" ? (
              <XAxis
                type="number"
                tickLine={{ stroke: "transparent" }}
                axisLine={{ stroke: "transparent" }}
                allowDecimals={false}
                domain={[0, maxValue || 1]}
                tickMargin={tickMargin}
                tickSize={tickSize}
                style={{
                  fontSize: "var(--font-xs)",
                  fill: "var(--text-subdued)",
                }}
                ticks={isRelative && [0, 25, 50, 75, 100]}
                orientation={labelYOrientation}
              />
            ) : (
              <XAxis
                tickLine={{ stroke: "transparent" }}
                axisLine={{ stroke: "transparent" }}
                type="category"
                dataKey={
                  showLegend ? (e, i) => getAlpha(e.initialOrder ?? i) : "label"
                }
                style={{
                  fontSize: "var(--font-xs)",
                  fill: "var(--text-subdued)",
                  hyphens: "auto",
                  wordBreak: "break-word",
                }}
                tickMargin={tickMargin}
                tickSize={tickSize}
              />
            )}
            {direction === "horizontal" ? (
              <YAxis
                type="category"
                dataKey="label"
                tickLine={{ stroke: "transparent" }}
                axisLine={{ stroke: "transparent" }}
                tickMargin={tickMargin}
                tickSize={tickSize}
                width={getYAxisWidth}
                style={{
                  fontSize: "var(--font-xs)",
                  fill: "var(--text-subdued)",
                  hyphens: "auto",
                  wordBreak: "break-word",
                }}
              />
            ) : (
              <YAxis
                type="number"
                tickLine={{ stroke: "transparent" }}
                axisLine={{ stroke: "transparent" }}
                tickMargin={tickMargin}
                tickSize={tickSize}
                width={getYAxisWidth}
                allowDecimals={false}
                domain={[0, maxValue || 1]}
                tick
                style={{
                  fontSize: "var(--font-xs)",
                  fill: "var(--text-subdued)",
                }}
              />
            )}
            {average ? (
              <ReferenceLine
                x={direction === "horizontal" ? average : undefined}
                y={direction === "vertical" ? average : undefined}
                stroke="var(--primary-500)"
                strokeWidth={2}
                strokeLinecap="round"
              />
            ) : null}

            <CartesianGrid
              horizontal={direction === "vertical"}
              vertical={direction === "horizontal"}
              className="stroke-border-subdued opacity-50"
            />
            <Tooltip
              content={<ChartTooltip />}
              payload={data}
              allowEscapeViewBox={{
                y: true,
              }}
              cursor={{ fill: "var(--action-secondary-default)" }}
              wrapperStyle={{ zIndex: 999 }}
            />
            {stacked ? (
              bars?.map((barCategory) => {
                return (
                  <Bar
                    dataKey={barCategory.value}
                    stackId={"a"}
                    key={`bar-${barCategory.value}`}
                    shape={(e) =>
                      renderRadius(e, data, bars, barCategory.value)
                    }
                    stroke="white"
                    strokeWidth={2}
                    radius={5}
                    fill={barCategory.fill || barCategory.color || "#fed957"}
                    isAnimationActive={isAnimationActive}
                  />
                );
              })
            ) : (
              <Bar
                dataKey="value"
                radius={
                  direction === "horizontal" ? [0, 5, 5, 0] : [5, 5, 0, 0]
                }
                isAnimationActive={isAnimationActive}
              >
                {data?.map((entry) => (
                  <Cell
                    stroke={"transparent"}
                    fill={entry.fill || entry.color || "#fed957"}
                    key={`cell-${entry.value}`}
                  />
                ))}
              </Bar>
            )}
          </BarChartRechart>
        </ResponsiveContainer>
        {showLegend && legendPayload && (
          <CustomizedLegend payload={legendPayload} />
        )}
      </div>
    )
  );
};

export default memo(BarChart);
